import { useEffect, useState } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { LogoMarque } from '../marque/LogoMarque';
import { useAuth } from '../auth/useAuth';
import { appelApi, ErreurApi } from '../lib/api';
import type { UtilisateurConnecte } from '../lib/types';
import { useTitrePage } from '../lib/useTitrePage';

interface ApercuLien {
  prenom: string;
  formation: string | null;
  piecesAttendues: {
    /** Code de la pièce au paramétrage : il l'identifie, y compris si l'organisme l'a ajoutée. */
    code: string;
    libelle: string;
    obligatoire: boolean;
    /** Nombre de fichiers à fournir : deux pour les photos d'identité. */
    nombreAttendu: number;
    /** « PDF, JPEG, PNG ou HEIC » : le candidat prépare le bon format. */
    libelleFormats: string;
  }[];
}

/** Longueur exigée par l'API : une seule politique dans l'application. */
const LONGUEUR_MINIMALE = 12;

/**
 * Ouverture de l'espace candidat depuis le lien reçu après l'entretien
 * (CDC §4.1).
 *
 * Seul écran de l'application accessible sans session : le candidat n'a pas
 * encore de compte, c'est ce qu'il vient créer. Le jeton est lu dans l'adresse
 * puis transmis dans le corps des requêtes — il ne repart jamais dans une URL
 * d'API, qui finirait dans les journaux du relais.
 *
 * L'écran affiche d'abord ce qu'on attend du candidat : découvrir la liste des
 * pièces après avoir choisi son mot de passe serait une mauvaise surprise.
 */
export function EspaceCandidatPage(): JSX.Element {
  useTitrePage('Espace candidat');
  const { jeton = '' } = useParams();
  const { utilisateur, installerSession } = useAuth();

  const [apercu, setApercu] = useState<ApercuLien | null>(null);
  const [lienRefuse, setLienRefuse] = useState<string | null>(null);
  const [verification, setVerification] = useState(true);

  const [motDePasse, setMotDePasse] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [erreur, setErreur] = useState<string | null>(null);
  const [enCours, setEnCours] = useState(false);

  useEffect(() => {
    let annule = false;
    setVerification(true);

    appelApi<ApercuLien>('/acces/verification', { methode: 'POST', corps: { jeton } })
      .then((reponse) => {
        if (!annule) setApercu(reponse);
      })
      .catch((cause) => {
        if (annule) return;
        setLienRefuse(
          cause instanceof ErreurApi
            ? cause.message
            : 'Ce lien n’a pas pu être vérifié. Réessayez plus tard.',
        );
      })
      .finally(() => {
        if (!annule) setVerification(false);
      });

    return () => {
      annule = true;
    };
  }, [jeton]);

  if (utilisateur) return <Navigate to="/dashboard" replace />;

  async function soumettre(evenement: React.FormEvent): Promise<void> {
    evenement.preventDefault();
    setErreur(null);

    if (motDePasse !== confirmation) {
      setErreur('Les deux mots de passe ne correspondent pas.');
      return;
    }
    if (motDePasse.length < LONGUEUR_MINIMALE) {
      setErreur(`Le mot de passe doit faire au moins ${LONGUEUR_MINIMALE} caractères.`);
      return;
    }

    setEnCours(true);
    try {
      const reponse = await appelApi<{ utilisateur: UtilisateurConnecte }>('/acces/activation', {
        methode: 'POST',
        corps: { jeton, motDePasse },
      });
      // La session est déjà ouverte par l'API : il n'y a qu'à la refléter.
      installerSession(reponse.utilisateur);
    } catch (cause) {
      setErreur(
        cause instanceof ErreurApi
          ? cause.message
          : 'L’espace n’a pas pu être ouvert. Réessayez plus tard.',
      );
    } finally {
      setEnCours(false);
    }
  }

  return (
    <div className="login-bg flex min-h-screen items-center justify-center p-4 sm:p-6">
      <div className="w-full max-w-2xl rounded-3xl bg-white p-4 sm:p-8 shadow-2xl md:p-10">
        <LogoMarque className="mb-8 h-9 w-auto" />

        {verification && <p className="text-sm text-codialis-gray">Vérification du lien…</p>}

        {lienRefuse && (
          <div>
            <h1 className="font-display text-2xl font-bold text-codialis-navy">
              Lien indisponible
            </h1>
            <p className="mt-2 text-sm text-codialis-gray">{lienRefuse}</p>
            <a
              href="/connexion"
              className="mt-6 inline-flex text-sm font-semibold text-codialis-sky hover:underline"
            >
              Aller à la page de connexion
            </a>
          </div>
        )}

        {apercu && (
          <>
            <h1 className="font-display text-2xl font-bold text-codialis-navy">
              Bienvenue {apercu.prenom}.
            </h1>
            <p className="mt-1 text-sm text-codialis-gray">
              Votre candidature est retenue
              {apercu.formation ? ` en ${apercu.formation}` : ''}. Choisissez un mot de passe
              pour ouvrir votre espace et y déposer vos pièces.
            </p>

            <div className="mt-6 rounded-2xl bg-codialis-sand p-4 sm:p-5">
              <h2 className="font-display text-sm font-bold text-codialis-navy">
                Pièces à préparer ({apercu.piecesAttendues.length})
              </h2>
              <ul className="mt-3 grid grid-cols-1 gap-1.5 sm:grid-cols-2">
                {apercu.piecesAttendues.map((piece) => (
                  <li
                    key={piece.code}
                    className="flex items-start gap-2 text-xs text-codialis-gray"
                  >
                    <span
                      aria-hidden="true"
                      className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-codialis-mint"
                    />
                    <span>
                      {piece.nombreAttendu > 1 ? `${piece.nombreAttendu} × ` : ''}
                      {piece.libelle}
                      {piece.obligatoire ? '' : ' (si concerné)'}
                      <span className="block text-[11px] text-codialis-gray/80">
                        {piece.libelleFormats}
                      </span>
                    </span>
                  </li>
                ))}
              </ul>
            </div>

            <form onSubmit={soumettre} className="mt-6 space-y-4" noValidate>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label
                    htmlFor="motdepasse"
                    className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-codialis-navy"
                  >
                    Mot de passe
                  </label>
                  <input
                    id="motdepasse"
                    type="password"
                    autoComplete="new-password"
                    required
                    minLength={LONGUEUR_MINIMALE}
                    value={motDePasse}
                    onChange={(evenement) => setMotDePasse(evenement.target.value)}
                    aria-describedby="regle-mot-de-passe"
                    className="w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm focus:border-transparent focus:outline-none focus:ring-2 focus:ring-codialis-mint"
                  />
                  <p id="regle-mot-de-passe" className="mt-1 text-[11px] text-codialis-gray">
                    {LONGUEUR_MINIMALE} caractères au minimum.
                  </p>
                </div>

                <div>
                  <label
                    htmlFor="confirmation"
                    className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-codialis-navy"
                  >
                    Confirmation
                  </label>
                  <input
                    id="confirmation"
                    type="password"
                    autoComplete="new-password"
                    required
                    value={confirmation}
                    onChange={(evenement) => setConfirmation(evenement.target.value)}
                    className="w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm focus:border-transparent focus:outline-none focus:ring-2 focus:ring-codialis-mint"
                  />
                </div>
              </div>

              {erreur && (
                <p role="alert" className="rounded-xl bg-red-50 px-4 py-3 text-sm text-red-700">
                  {erreur}
                </p>
              )}

              <button
                type="submit"
                disabled={enCours}
                className="w-full rounded-xl bg-codialis-navy px-4 py-3.5 text-sm font-semibold text-white transition hover:bg-codialis-deep disabled:opacity-60"
              >
                {enCours ? 'Ouverture…' : 'Ouvrir mon espace'}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
